import React, { useState, useEffect, useContext } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import { withRouter } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import NotFound from "./pages/not-found/not-found.component";
import LoginPage from "./pages/login/login.component";
import FloorsPage from "./pages/floors/floors.component";
import SpaceInfo from "./pages/space-info/space-info.component";
import Floor from "./pages/floor/floor.component";
import Area from "./pages/area/area.component";
import TimeTable from "./pages/timetable/timetable.component";
import NotificationPage from "./pages/notifications/notification.component";
import UserNotifications from "./pages/user-notifications/user-notifications.component";
import ManageSystem from "./pages/manage-system/manage-system.component";
import ManagePermissions from "./pages/manage-permissions/manage-permissions.component";
import EditSpacePage from "./pages/edit-space/edit-space.component";
import SystemInfo from "./pages/system-info/system-info.component";
import FilterSpacePage from "./pages/filter-space/filter-space.component";
import Reports from "./pages/reports/reports.component";
import DeleteReservation from "./pages/delete-reservation/delete-reservation.component";
import ManageReservationReason from "./pages/manage-reservation-reason/manage-reservation-reason.component";
import ConsultSpaceManager from "./pages/consult-space-manager/consult-space-manager.component";
import ProfilePage from "./pages/profile/profile.component";
import ManageEmployees from "./pages/menage-employees/manage-employees.component";
import ManageSchools from "./pages/manage-schools/manage-schools.component";
import ImportOfSummaries from "./pages/import-of-summaries/import-of-summaries.component";
import ExamsPage from "./pages/exams/exams.component";
import ZoomMeetingsPage from "./pages/zoom-meetings/zoom-meetings.component";
import ZoomStartPage from "./pages/zoom-start/zoom-start.component";

import "./App.scss";
import NavbarCustom from "./components/navbar/navbar.component";

import { GlobalContext } from "./GlobalState";
import http from "./api/http";
import { useAuth } from "./auth/AuthProvider";

const App = ({ history }) => {
  const dispatch = useDispatch();
  const employeeName = useSelector((state) => state.employee.employeeName);
  const employeeRole = useSelector((state) => state.employee.employeeRole);

  const {
    ready: authReady,
    authenticated: kcAuthenticated,
    login: kcLogin,
    logout: kcLogout,
  } = useAuth();

  const [systemMasterManager, setSystemMasterManager] = useState(false);
  const [systemManager, setSystemManager] = useState(false);
  const [spaceManager, setSpaceManager] = useState(false);
  const [isUser, setIsUser] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState("");

  const { selectedSchoolCode, setSelectedSchoolCode } =
    useContext(GlobalContext);
  const { setUserSchoolCode } = useContext(GlobalContext);

  useEffect(() => {
    setSystemMasterManager(employeeRole === "system-master-manager");
    setSystemManager(employeeRole === "system-manager");
    setSpaceManager(employeeRole === "space-manager");
    setIsUser(employeeRole === "user");
  }, [employeeRole]);

  useEffect(() => {
    if (!authReady) return;
    setIsAuthenticated(!!kcAuthenticated);
    if (!kcAuthenticated) {
      dispatch({ type: "SET_EMPLOYEE_ROLE", payload: "" });
      dispatch({ type: "SET_EMPLOYEE_NAME", payload: "" });
      dispatch({ type: "SET_EMPLOYEE_ID", payload: "" });
      setUserName("");

      setSelectedSchoolCode(0);
      setUserSchoolCode(0);
      localStorage.removeItem("savedSchoolCode");
    }
  }, [
    authReady,
    kcAuthenticated,
    dispatch,
    setSelectedSchoolCode,
    setUserSchoolCode,
  ]);

  useEffect(() => {
    if (!authReady || !kcAuthenticated) return;

    const fetchEmployeeData = async () => {
      try {
        const response = await http.get("employee"); // baseURL + "employee"
        const { role, name, schoolCode, _id } = response.data.user;

        dispatch({ type: "SET_EMPLOYEE_ROLE", payload: role });
        dispatch({ type: "SET_EMPLOYEE_NAME", payload: name });
        dispatch({ type: "SET_EMPLOYEE_ID", payload: _id });
        setUserName(name);

        let savedSchoolCode = localStorage.getItem("savedSchoolCode");

        if (schoolCode) {
          if (!savedSchoolCode) {
            setSelectedSchoolCode(schoolCode);
            localStorage.setItem("savedSchoolCode", String(schoolCode));
          }
          setUserSchoolCode(schoolCode);
        } else {
          const schoolResponse = await http.get("school");
          const firstCode = schoolResponse.data.data.allSchools[0].code;

          if (!savedSchoolCode) {
            setSelectedSchoolCode(firstCode);
            localStorage.setItem("savedSchoolCode", String(firstCode));
          }
          setUserSchoolCode(firstCode);
        }

        savedSchoolCode = localStorage.getItem("savedSchoolCode");
        setSelectedSchoolCode(Number(savedSchoolCode));
      } catch (error) {
        console.error("Failed to fetch employee data:", error);
        // Check possibility to make login
        // kcLogin();
      }
    };

    fetchEmployeeData();
  }, [
    authReady,
    kcAuthenticated,
    dispatch,
    setSelectedSchoolCode,
    setUserSchoolCode,
  ]);

  const logout = async () => {
    try {
      await http.get("employee/logout");
    } catch (e) {
      // ignore backend logout failures; keycloak logout still works
    } finally {
      setIsAuthenticated(false);
      setSystemMasterManager(false);
      setSystemManager(false);
      setSpaceManager(false);
      setIsUser(false);
      setSelectedSchoolCode(0);
      setUserSchoolCode(0);
      localStorage.removeItem("savedSchoolCode");

      kcLogout();
    }
  };

  const setAuthenticated = () => {
    if (!kcAuthenticated) kcLogin();
  };

  if (!authReady) {
    return <div style={{ padding: 16 }}>Loading authentication…</div>;
  }

  const DefaultRoutes = () => (
    <div className="App">
      <NavbarCustom
        isAuthenticated={isAuthenticated}
        systemMasterManager={systemMasterManager}
        systemManager={systemManager}
        spaceManager={spaceManager}
        employeeName={employeeName}
        isUser={isUser}
        logout={logout}
      />

      <div className="content">
        <Switch>
          {isAuthenticated ? (
            <Route exact path="/">
              <Redirect to="/home" />
            </Route>
          ) : (
            <Route exact path="/" component={() => <LoginPage />} />
          )}

          <Route exact path="/home" component={() => <FilterSpacePage />} />

          <Route
            exact
            path="/notifications"
            component={() =>
              spaceManager || systemManager || systemMasterManager ? (
                <NotificationPage isSystemMasterManager={systemMasterManager} />
              ) : isUser ? (
                <UserNotifications />
              ) : null
            }
          />

          <Route
            exact
            path="/manage-system"
            component={
              systemManager || systemMasterManager
                ? ManageSystem
                : spaceManager
                  ? EditSpacePage
                  : null
            }
          />

          <Route
            exact
            path="/permissions"
            component={
              systemManager || systemMasterManager ? ManagePermissions : null
            }
          />

          <Route
            exact
            path="/spreadsheets"
            component={
              spaceManager || systemManager || systemMasterManager
                ? Reports
                : null
            }
          />

          <Route
            exact
            path="/employees"
            component={systemMasterManager ? ManageEmployees : null}
          />

          <Route
            exact
            path="/schools"
            component={systemMasterManager ? ManageSchools : null}
          />

          <Route
            exact
            path="/delete-reservation"
            component={systemMasterManager ? DeleteReservation : null}
          />

          <Route
            exact
            path="/force-import-of-summaries"
            component={systemMasterManager ? ImportOfSummaries : null}
          />

          <Route
            exact
            path="/reservationReasons"
            component={
              systemManager || systemMasterManager
                ? ManageReservationReason
                : null
            }
          />

          <Route
            exact
            path="/myspaces"
            component={
              systemManager || systemMasterManager || spaceManager
                ? () => (
                  <ConsultSpaceManager
                    isAdmin={systemManager || systemMasterManager}
                  />
                )
                : null
            }
          />

          {isAuthenticated ? (
            <Route exact path="/profile" component={() => <ProfilePage />} />
          ) : null}

          <Route exact path="/findFloor" component={FloorsPage} />

          {isAuthenticated ? (
            <Route exact path="/exams" component={ExamsPage} />
          ) : null}

          <Route
            exact
            path="/zoom-meetings"
            component={
              systemManager || systemMasterManager || spaceManager
                ? () => <ZoomMeetingsPage />
                : null
            }
          />

          {isAuthenticated ? (
            <Route exact path="/zoom-start/:reservId" component={ZoomStartPage} />
          ) : null}

          <Route exact path="/info" component={SystemInfo} />
          <Route exact path="/floor/:floorId" component={Floor} />
          <Route exact path="/floor/:floorId/area/:areaId" component={Area} />
          <Route
            exact
            path="/floor/:floorId/area/:areaId/spaceinfo/:spaceinfoId"
            component={SpaceInfo}
          />
          <Route
            exact
            path="/floor/:floorId/area/:areaId/timetable/:timetableId"
            component={TimeTable}
          />
        </Switch>
      </div>
    </div>
  );

  if (!authReady) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  return (
    <Switch>
      <Route component={NotFound} path="/notfound" />
      <Route component={DefaultRoutes} />
    </Switch>
  );
};

export default withRouter(App);
